import { useAuth0 } from '@auth0/auth0-react';
import { useEffect, useState, useCallback } from 'react';
import { httpServices, setAuth0TokenGetter } from '@/api/client';
import { AppConstants, COOKIE_MAX_AGE } from '@/utils/constants';
import { setAuth0UserId } from '@/services/auth0UserStore';

// Helper function to add timeout to a promise to prevent infinite waiting for the token
const withTimeout = <T>(
  promise: Promise<T>,
  timeoutMs: number,
  errorMessage: string,
): Promise<T> => {
  const timeoutPromise = new Promise<never>((_, reject) => {
    setTimeout(() => {
      reject(new Error(errorMessage));
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise]);
};

export const useAuth0Token = () => {
  const { getAccessTokenSilently, isAuthenticated, user, isLoading } =
    useAuth0();
  const [tokenReady, setTokenReady] = useState(false);

  const getToken = useCallback(async (): Promise<string> => {
    try {
      return await withTimeout(
        getAccessTokenSilently(),
        3000,
        'Token retrieval timeout after 3 seconds',
      );
    } catch (error) {
      throw error;
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    setAuth0TokenGetter(getToken);
  }, [getToken]);

  useEffect(() => {
    const initializeToken = async () => {
      if (isLoading) {
        setTokenReady(false);
        return;
      }

      if (!isAuthenticated) {
        setTokenReady(false);
        return;
      }

      try {
        const token = await withTimeout(
          getAccessTokenSilently(),
          3000, // 3 seconds timeout
          'Token initialization timeout after 3 seconds',
        );

        if (token) {
          httpServices.llmService.updateToken(token);
          httpServices.saasService.updateToken(token);

          if (user?.sub) {
            setAuth0UserId(user.sub);
          }

          localStorage.removeItem('access_token');
          setTokenReady(true);
        } else {
          setTokenReady(false);
        }
      } catch (error) {
        setTokenReady(false);
      }
    };

    initializeToken();
  }, [isAuthenticated, isLoading, getAccessTokenSilently, user]);

  return {
    tokenReady,
    isAuthenticated,
    user,
    isLoading,
    getAccessTokenSilently: getToken,
  };
};
